import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Shield, AlertTriangle, Flag, UserX, MessageSquare, 
  Eye, Check, X, Ban
} from 'lucide-react';

const API_URL = window.location.origin;

const AdminSafetyDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('reports');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/safety/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch safety dashboard:', error);
      toast.error('Failed to load safety dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleResolveReport = async (reportId, action) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/safety/resolve-report/${reportId}?action=${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Report resolved: ${action}`);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to resolve report');
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading safety dashboard...</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-safety-dashboard">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <Flag className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-forest">{data?.stats?.pending_reports || 0}</p>
                <p className="text-sm text-gray-500">Pending Reports</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-forest">{data?.stats?.flagged_content_count || 0}</p>
                <p className="text-sm text-gray-500">Flagged Content</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <UserX className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-forest">{data?.stats?.suspended_users_count || 0}</p>
                <p className="text-sm text-gray-500">Suspended Users</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Shield className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-forest">{data?.stats?.total_reports_today || 0}</p>
                <p className="text-sm text-gray-500">Reports Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="reports">
            Pending Reports
            {data?.pending_reports?.length > 0 && (
              <Badge className="ml-2 bg-red-500 text-white text-xs">{data.pending_reports.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="flagged">Flagged Content</TabsTrigger>
          <TabsTrigger value="users">Flagged Users</TabsTrigger>
          <TabsTrigger value="suspended">Suspended</TabsTrigger>
        </TabsList>

        {/* Pending Reports */}
        <TabsContent value="reports" className="mt-4">
          {data?.pending_reports?.length > 0 ? (
            <div className="space-y-4">
              {data.pending_reports.map((report) => (
                <Card key={report.id} className="card-base">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-xs">
                            {report.content_type}
                          </Badge>
                          <Badge 
                            className={
                              report.reason === 'solicitation' ? 'bg-red-100 text-red-700' :
                              report.reason === 'harassment' ? 'bg-purple-100 text-purple-700' :
                              report.reason === 'unsafe' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'
                            }
                          >
                            {report.reason}
                          </Badge>
                          {report.content_type === 'admin_flag' && (
                            <Badge className="bg-purple-600 text-white text-xs">Admin Flag</Badge>
                          )}
                        </div>
                        
                        {/* Reported User */}
                        <div className="flex items-center gap-2 mb-2 p-2 bg-red-50 rounded-lg">
                          <UserX className="h-4 w-4 text-red-500" />
                          <span className="text-sm font-medium text-red-700">Reported:</span>
                          <span className="text-sm text-red-800">{report.reported_user_name || 'Unknown'}</span>
                          <span className="text-xs text-red-500">({report.reported_user_email || report.reported_user_id})</span>
                        </div>
                        
                        {/* Reporter */}
                        <div className="flex items-center gap-2 mb-2 text-sm text-gray-600">
                          <Flag className="h-3 w-3" />
                          <span>Reported by: <strong>{report.reporter_name || 'Anonymous'}</strong></span>
                          {report.reporter_email && <span className="text-xs text-gray-400">({report.reporter_email})</span>}
                        </div>
                        
                        {report.details && (
                          <p className="text-sm text-gray-500 bg-sand/50 p-2 rounded mt-2">
                            &ldquo;{report.details}&rdquo;
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          Reported: {new Date(report.created_at).toLocaleString()}
                        </p>
                      </div>

                      <div className="flex flex-col gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResolveReport(report.id, 'dismiss')}
                          className="text-gray-500"
                        >
                          <X className="h-4 w-4 mr-1" /> Dismiss
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResolveReport(report.id, 'warn')}
                          className="text-amber-600 border-amber-200"
                        >
                          <AlertTriangle className="h-4 w-4 mr-1" /> Warn
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResolveReport(report.id, 'restrict')}
                          className="text-orange-600 border-orange-200"
                        >
                          <MessageSquare className="h-4 w-4 mr-1" /> Restrict DMs
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleResolveReport(report.id, 'suspend')}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          <Ban className="h-4 w-4 mr-1" /> Suspend
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-base">
              <CardContent className="py-12 text-center">
                <Check className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <p className="text-gray-500">No pending reports. Community is healthy!</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Flagged Content */}
        <TabsContent value="flagged" className="mt-4">
          {data?.flagged_content?.length > 0 ? (
            <div className="space-y-4">
              {data.flagged_content.map((item) => (
                <Card key={item.id} className="card-base">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">{item.content_type}</Badge>
                      <Badge className="bg-amber-100 text-amber-700">Auto-Flagged</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>User:</strong> {item.user_id}
                    </p>
                    <p className="text-sm bg-red-50 p-2 rounded border border-red-100">
                      {item.content}
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      Flags: {item.flags?.join(', ')}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-base">
              <CardContent className="py-12 text-center">
                <Check className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <p className="text-gray-500">No flagged content</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Flagged Users */}
        <TabsContent value="users" className="mt-4">
          {data?.flagged_users?.length > 0 ? (
            <div className="space-y-4">
              {data.flagged_users.map((user) => (
                <Card key={user.id} className="card-base">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-forest">{user.full_name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className="bg-red-100 text-red-700">
                            {user.report_count} report(s)
                          </Badge>
                          {user.dm_suspended && (
                            <Badge className="bg-orange-100 text-orange-700">DMs Restricted</Badge>
                          )}
                        </div>
                      </div>
                      <Button size="sm" variant="outline">
                        <Eye className="h-4 w-4 mr-1" /> View Profile
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-base">
              <CardContent className="py-12 text-center">
                <Check className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <p className="text-gray-500">No flagged users</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Suspended Users */}
        <TabsContent value="suspended" className="mt-4">
          {data?.suspended_users?.length > 0 ? (
            <div className="space-y-4">
              {data.suspended_users.map((user) => (
                <Card key={user.id} className="card-base border-red-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-forest">{user.full_name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <p className="text-xs text-red-500 mt-1">
                          Suspended: {user.suspended_at ? new Date(user.suspended_at).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" className="text-green-600 border-green-200">
                        Reinstate
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-base">
              <CardContent className="py-12 text-center">
                <Check className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <p className="text-gray-500">No suspended users</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSafetyDashboard;
